import { __awaiter, __generator, __read, __spreadArray } from "tslib";
import { OfficeActionId } from '@office-start/action-types';
import { runUnregisteredAction, useDocumentActionsCreator, } from '@office-start/actions-service';
import { useCapabilities } from '@office-start/capabilities-service';
import { getInstrumentedActionId } from '@office-start/document-actions-service';
import { ActionContextMenuItemIcon, FileType, KnownDocumentActionIdPrefix, } from '@office-start/document-types';
import { IconNames } from '@office-start/fabric-icon-subset-utility';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { logFeature } from '@office-start/telemetry-service';
import { useToastActionCreators } from '@office-start/toast-utility';
import React, { lazy, Suspense, useCallback } from 'react';
import { logTagAction } from '../utilities/logTagAction';
import { useOpenEditLinkDialog } from './useEditLinkDialog';
var LazyTagPickerMenuControl = lazy(function () { return import('@office-start/tag-picker-menu-control'); });
/**
 * This hook will return a callback to generate a set of Actions for the Actions Menu for the documents in the Edgeworth section.
 */
export function useEdgeworthDocumentActionsCreator(_a) {
    var _this = this;
    var _b, _c;
    var togglePinnedState = _a.togglePinnedState, toggleHiddenState = _a.toggleHiddenState, instrumentAction = _a.instrumentAction, getCommonActionInstrumentation = _a.getCommonActionInstrumentation, locStrings = _a.locStrings, tags = _a.tags, navigateToMyContent = _a.navigateToMyContent;
    var capabilities = useCapabilities().data;
    var featureGates = useFeatureGates().data;
    var documentActionsCreator = useDocumentActionsCreator();
    var _d = useToastActionCreators(), clearNotification = _d.clearNotification, createErrorNotification = _d.createErrorNotification, createLoadingNotification = _d.createLoadingNotification, createSuccessNotification = _d.createSuccessNotification;
    var openEditLinkDialog = useOpenEditLinkDialog();
    var actionsCreator = useCallback(function (item, index, itemElementId) {
        var _a, _b;
        if (!capabilities || !documentActionsCreator || !locStrings) {
            return {
                actionMenuItems: [],
                commandItems: [],
            };
        }
        var edgeworthStringResources = locStrings.edgeworthResources;
        var taggingResources = locStrings.edgeworthTaggingResources;
        var hideFromListTitle = edgeworthStringResources.hideActionTitle;
        var unhideFromListTitle = edgeworthStringResources.unhideActionTitle;
        var addToFavoritesTitle = capabilities.contentActionsEnabled
            ? edgeworthStringResources.addToFavoritesActionTitle20
            : edgeworthStringResources.addToFavoritesActionTitle;
        var removeFromFavoritesTitle = capabilities.contentActionsEnabled
            ? edgeworthStringResources.removeFromFavoritesActionTitle20
            : edgeworthStringResources.removeFromFavoritesActionTitle;
        var additionalActions = [];
        // Instrumentation for content actions (Pin/Unpin, Hide/Unhide)
        var _c = getCommonActionInstrumentation(item, index), instrumentationParams = _c.instrumentationParams, instrumentationArea = _c.instrumentationArea;
        var tagAction;
        if (tags && !item.isLocalFile) {
            var tagItems_1 = Array.from(tags.tagIdToItemsDocumentIdsMap.values()).map(function (tuple) { return tuple[0]; });
            var currentDocumentTags_1;
            if (tags.docIdToTagIdsMap.has(item.id)) {
                var currentDocumentTagIds = Array.from(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                tags.docIdToTagIdsMap.get(item.id));
                currentDocumentTags_1 = currentDocumentTagIds
                    .map(function (tagId) {
                    return tags.tagIdToItemsDocumentIdsMap.has(tagId)
                        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            tags.tagIdToItemsDocumentIdsMap.get(tagId)[0]
                        : undefined;
                })
                    .filter(function (item) { return !!item; });
            }
            var wrappedOnTagCreated_1 = function (tagItem, isNewTag) {
                if (isNewTag) {
                    createLoadingNotification(taggingResources.createTagLoadingNotificationText);
                }
                logTagAction(instrumentationArea, 'TagCreate', 'Create', tagItem.color);
                logFeature({
                    featureName: "HarmonyTag" /* KnownFeatureNames.HarmonyTag */,
                    eventName: 'TagCreateDebug',
                    params: {
                        DocumentId: item.id,
                        IsNewTag: !!isNewTag,
                    },
                });
                var createTagPromise = tags.createTag(tagItem, item);
                if (isNewTag) {
                    createTagPromise
                        .then(function () {
                        clearNotification();
                        createSuccessNotification(taggingResources.createTagSuccessNotificationText);
                    })
                        .catch(function () {
                        clearNotification();
                        createErrorNotification(taggingResources.createTagFailureNotificationText);
                    });
                }
            };
            var wrappedOnTagRemoved_1 = function (tagItem) {
                logTagAction(instrumentationArea, 'TagRemove', 'Remove');
                logFeature({
                    featureName: "HarmonyTag" /* KnownFeatureNames.HarmonyTag */,
                    eventName: 'TagRemoveDebug',
                    params: {
                        DocumentId: item.id,
                    },
                });
                tags.removeTag(tagItem, item);
            };
            var wrapperNavigateToMyContent_1 = instrumentationArea !== 'MyContent' && navigateToMyContent
                ? function () {
                    return navigateToMyContent('ContentActions', 'tag_action_footer');
                }
                : undefined;
            tagAction = {
                // title and ariaLabel are currently not used since we're rendering a custom menu
                title: taggingResources.tagText,
                ariaLabel: taggingResources.tagText,
                id: KnownDocumentActionIdPrefix.tag,
                renderMenu: function () { return (React.createElement(Suspense, { fallback: null },
                    React.createElement(LazyTagPickerMenuControl, { onInitialMenuRender: function () {
                            return logTagAction('ContentActions', 'TagMenuItem', 'Select');
                        }, onTagCreated: wrappedOnTagCreated_1, onTagRemoved: wrappedOnTagRemoved_1, tagItems: tagItems_1, navigateToMyContent: wrapperNavigateToMyContent_1, defaultSelectedTags: currentDocumentTags_1, taggingResources: taggingResources }))); },
            };
        }
        if (item.fileType !== FileType.MeetingObject && !item.isLocalFile) {
            var favoriteUnfavoriteAction = item.isPinned
                ? {
                    title: removeFromFavoritesTitle,
                    ariaLabel: removeFromFavoritesTitle,
                    id: KnownDocumentActionIdPrefix.unfavorite,
                    executeAction: function () {
                        if (documentActionsCreator.actionExecutor &&
                            capabilities.contentActionsEnabled) {
                            runUnregisteredAction({
                                actionExecutor: documentActionsCreator.actionExecutor,
                                actionId: OfficeActionId.Unfavorite,
                                executeAction: function () {
                                    return togglePinnedState({
                                        document: item,
                                        isPinning: false,
                                    });
                                },
                                notificationOptions: {
                                    error: {
                                        message: locStrings
                                            .documentActionsToastResources
                                            .removedFromFavoritesFailed,
                                    },
                                    loading: {
                                        showLoadingIndicator: false,
                                    },
                                    success: {
                                        message: locStrings
                                            .documentActionsToastResources
                                            .removedFromFavorites,
                                    },
                                },
                                instrumentationArea: instrumentationArea,
                                params: instrumentationParams,
                            });
                        }
                        else {
                            togglePinnedState({
                                document: item,
                                isPinning: false,
                            });
                        }
                    },
                    icon: IconNames.Unfavorite,
                    contentActionOptions: {
                        icon: {
                            type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                            iconName: 'StarOffIcon',
                        },
                    },
                }
                : {
                    title: addToFavoritesTitle,
                    ariaLabel: addToFavoritesTitle,
                    id: KnownDocumentActionIdPrefix.favorite,
                    executeAction: function () {
                        if (documentActionsCreator.actionExecutor &&
                            capabilities.contentActionsEnabled) {
                            runUnregisteredAction({
                                actionExecutor: documentActionsCreator.actionExecutor,
                                actionId: OfficeActionId.Favorite,
                                executeAction: function () {
                                    return togglePinnedState({
                                        document: item,
                                        isPinning: true,
                                    });
                                },
                                notificationOptions: {
                                    error: {
                                        message: locStrings
                                            .documentActionsToastResources
                                            .addedToFavoritesFailed,
                                    },
                                    loading: {
                                        showLoadingIndicator: false,
                                    },
                                    success: {
                                        message: locStrings
                                            .documentActionsToastResources
                                            .addedToFavorites,
                                    },
                                },
                                instrumentationArea: instrumentationArea,
                                params: instrumentationParams,
                            });
                        }
                        else {
                            togglePinnedState({
                                document: item,
                                isPinning: true,
                            });
                        }
                    },
                    icon: IconNames.FavoriteStar,
                    contentActionOptions: {
                        icon: {
                            type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                            iconName: 'StarIcon',
                        },
                    },
                };
            var hideUnhideAction = item.isHidden
                ? {
                    title: unhideFromListTitle,
                    ariaLabel: unhideFromListTitle,
                    id: KnownDocumentActionIdPrefix.unhide,
                    executeAction: function () {
                        if (documentActionsCreator.actionExecutor &&
                            capabilities.contentActionsEnabled) {
                            runUnregisteredAction({
                                actionExecutor: documentActionsCreator.actionExecutor,
                                actionId: OfficeActionId.Unhide,
                                executeAction: function () {
                                    return toggleHiddenState({
                                        document: item,
                                        isHiding: false,
                                    });
                                },
                                notificationOptions: {
                                    error: {
                                        message: locStrings
                                            .documentActionsToastResources
                                            .itemShownFailed,
                                    },
                                    loading: {
                                        showLoadingIndicator: false,
                                    },
                                    success: {
                                        message: locStrings
                                            .documentActionsToastResources
                                            .itemShown,
                                    },
                                },
                                instrumentationArea: instrumentationArea,
                                params: instrumentationParams,
                            });
                        }
                        else {
                            toggleHiddenState({
                                document: item,
                                isHiding: false,
                            });
                        }
                    },
                    icon: IconNames.Hide,
                    contentActionOptions: {
                        icon: {
                            type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                            iconName: 'HideIcon',
                        },
                    },
                }
                : {
                    title: hideFromListTitle,
                    ariaLabel: hideFromListTitle,
                    id: KnownDocumentActionIdPrefix.hide,
                    executeAction: function () {
                        if (documentActionsCreator.actionExecutor &&
                            capabilities.contentActionsEnabled) {
                            runUnregisteredAction({
                                actionExecutor: documentActionsCreator.actionExecutor,
                                actionId: OfficeActionId.Hide,
                                executeAction: function () {
                                    return toggleHiddenState({
                                        document: item,
                                        isHiding: true,
                                    });
                                },
                                notificationOptions: {
                                    error: {
                                        message: locStrings
                                            .documentActionsToastResources
                                            .itemHiddenFailed,
                                    },
                                    loading: {
                                        showLoadingIndicator: false,
                                    },
                                    success: {
                                        message: locStrings
                                            .documentActionsToastResources
                                            .itemHidden,
                                    },
                                },
                                instrumentationArea: instrumentationArea,
                                params: instrumentationParams,
                            });
                        }
                        else {
                            toggleHiddenState({
                                document: item,
                                isHiding: true,
                            });
                        }
                    },
                    icon: IconNames.Hide,
                    contentActionOptions: {
                        icon: {
                            type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                            iconName: 'HideIcon',
                        },
                    },
                };
            additionalActions.push(favoriteUnfavoriteAction);
            if (tagAction) {
                additionalActions.push(tagAction);
            }
            additionalActions.push(hideUnhideAction);
        }
        else if (tagAction) {
            additionalActions.push(tagAction);
        }
        if (((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.edgeworthConfigGates) === null || _b === void 0 ? void 0 : _b.edgeworthConfigLinksEnabled) &&
            item.fileType === FileType.Link &&
            item.driveId &&
            item.docId) {
            additionalActions.push({
                title: 'Edit',
                ariaLabel: 'Edit',
                id: KnownDocumentActionIdPrefix.edit,
                executeAction: function () {
                    if (documentActionsCreator.actionExecutor) {
                        runUnregisteredAction({
                            actionExecutor: documentActionsCreator.actionExecutor,
                            actionId: OfficeActionId.Edit,
                            executeAction: function () { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                                return [2 /*return*/, openEditLinkDialog(item)];
                            }); }); },
                            notificationOptions: undefined,
                            instrumentationArea: instrumentationArea,
                            params: instrumentationParams,
                        });
                    }
                },
                contentActionOptions: {
                    icon: {
                        type: ActionContextMenuItemIcon.ActionContextMenuIcon,
                        iconName: 'EditIcon',
                    },
                },
            });
        }
        var targetUX = { targetElementId: itemElementId };
        var standardDocumentActions = documentActionsCreator.createDocumentActions(item, index, targetUX, instrumentationArea, 'edgeworth-list-menu');
        var commandItems = __spreadArray([], __read(documentActionsCreator.createCommandActions(item, index, targetUX, instrumentationArea, 'edgeworth-list-topLevel')), false);
        // Filter out some actions based on additional Edgeworth item properties
        // TODO: Safelink might not be returned from EW APIs anymore
        if (item.isSafeLink || !item.url) {
            var filterById_1 = function (lowerDocumentActionId, actionIds) {
                return actionIds.every(function (actionId) {
                    return lowerDocumentActionId.indexOf(actionId.toLowerCase()) < 0;
                });
            };
            var urlActionFilters_1 = [
                OfficeActionId.Share,
                OfficeActionId.CopyLink,
                OfficeActionId.OpenInApp,
            ];
            var safelinkActionFilters_1 = [
                OfficeActionId.Download,
                OfficeActionId.OpenFileLocation,
                OfficeActionId.OpenInApp,
            ];
            var filterDocumentActions_1 = function (documentActions) {
                return documentActions.filter(function (documentAction) {
                    if (documentAction.subMenus) {
                        documentAction.subMenus = filterDocumentActions_1(documentAction.subMenus);
                    }
                    var lowerCaseId = documentAction.id.toLowerCase();
                    // Once content actions are enabled for all users url filters can be removed from this file
                    if (!item.url &&
                        !capabilities.contentActionsEnabled) {
                        return filterById_1(lowerCaseId, urlActionFilters_1);
                    }
                    else if (item.isSafeLink) {
                        return filterById_1(lowerCaseId, safelinkActionFilters_1);
                    }
                    return true;
                });
            };
            standardDocumentActions = filterDocumentActions_1(standardDocumentActions);
            commandItems = filterDocumentActions_1(commandItems);
        }
        var actionMenuItems = standardDocumentActions.concat(additionalActions);
        // When contentActionsEnabled is on, command items are created and instrumented above (Favorite is no longer in the command bar)
        if (!capabilities.contentActionsEnabled) {
            actionMenuItems.forEach(function (action) {
                var id = action.id;
                var instrumentedActionId = getInstrumentedActionId(id);
                if (instrumentedActionId) {
                    // Share and favorite/unfavorite are also shown when hovering over an Edgeworth row, and are same with the ones in right-click
                    // action menu or '...' menu. The only difference is how they are instrumented, so cloning the actions here and wrap instrumentation code
                    // around the existing action callbacks
                    if (id === KnownDocumentActionIdPrefix.share ||
                        id === KnownDocumentActionIdPrefix.favorite ||
                        id === KnownDocumentActionIdPrefix.unfavorite) {
                        // Clone from action
                        var commandItem = Object.assign({}, action);
                        var commandItemOriginalAction_1 = commandItem.executeAction;
                        if (commandItemOriginalAction_1) {
                            commandItem.executeAction = function (event) {
                                if (event) {
                                    event.preventDefault();
                                }
                                commandItemOriginalAction_1(event);
                                instrumentAction("".concat(instrumentedActionId, "_topLevel"), item, index);
                            };
                        }
                        commandItems.push(commandItem);
                    }
                    var originalAction_1 = action.executeAction;
                    if (originalAction_1) {
                        action.executeAction = function (event) {
                            originalAction_1(event);
                            instrumentAction("".concat(instrumentedActionId, "_menu"), item, index);
                        };
                    }
                }
            });
        }
        return {
            actionMenuItems: actionMenuItems,
            commandItems: commandItems,
            contentActionsEnabled: capabilities.contentActionsEnabled,
            // Pass along grouping information (strings, icon) to menu UX
            contentActionsGroupInfo: documentActionsCreator.getContentActionsGroupInfo(),
        };
    }, [
        capabilities,
        documentActionsCreator,
        locStrings,
        getCommonActionInstrumentation,
        tags,
        (_c = (_b = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _b === void 0 ? void 0 : _b.edgeworthConfigGates) === null || _c === void 0 ? void 0 : _c.edgeworthConfigLinksEnabled,
        navigateToMyContent,
        createLoadingNotification,
        clearNotification,
        createSuccessNotification,
        createErrorNotification,
        togglePinnedState,
        toggleHiddenState,
        openEditLinkDialog,
        instrumentAction,
    ]);
    return actionsCreator;
}
//# sourceMappingURL=useEdgeworthDocumentActionsCreator.js.map